import { isNull } from 'lodash/fp';
import { toast } from 'react-toastify';
import { push } from 'redux-first-history';
import { all, call, put, takeLeading, select, takeLatest } from 'redux-saga/effects';
import download from 'downloadjs';

import { askPortfolioQuestion, deletePortfolioAnalysisTemplate, downloadPortfolioAnalytics, getPortfolioAnalysisTemplates, getPortfolioFields, savePortfolioAnalysisTemplate } from '../../../../services/analytics';
import { dotsToDashes } from '../../../../utils/regex-utils';
import { openDocumentAndInstance } from '../../../documents/my-documents/store/actions';
import { fetchDocumentFieldsStarted, fetchDocumentFieldsSuccessful, fetchDocumentFieldsFailed, updatePortfolioQuestion, askPortfolioQuestionStarted, askPortfolioQuestionFailed, askPortfolioQuestionSuccessful, redirectToDocument, downloadPortfolioAnalyticsFailed, downloadPortfolioAnalyticsSuccessful, toggleDownloadModal, setPieChartBase64, setFileName, getPortfolioAnalyticsTemplatesSuccessful, getPortfolioAnalyticsTemplatesFailed, deletePortfolioAnalyticsTemplateStarted, deletePortfolioAnalyticsTemplateFailed, deletePortfolioAnalyticsTemplateSuccessful, savePortfolioAnalyticsTemplateStarted, savePortfolioAnalyticsTemplateSuccessful, savePortfolioAnalyticsTemplateFailed, setPortfolioAnalyticsTemplate } from './actions';
import { getDownloadFilename, getPieChartBase64, getGraphKeyColours, getPortfolioAnalyticsTemplateUsed, getPortfolioQuestion, getPortfolioResponse } from './selectors';
import { PortfolioAnalysisTemplate, PortfolioAnalyticsActionTypes, PortfolioDocumentField, PortfolioQuestion, PortfolioQuestionResponse } from './types';
import { GraphKeyAndColour } from '../../../shared/analytics/PieChart';
import { getAllClientDatasets } from '../../client-datasets/store/selectors';
import { ClientDatasets } from '../../client-datasets/store/types';
import { DocumentNameDB } from '../../documents/store/types';
import { getAllDocumentNames } from '../../documents/store/selectors';
import { DatasetType } from '../../../datasets/store/types';
import { dateNow, formatDate } from '../../../../utils/luxon';
import { getChartBase64String } from '../../../../utils/analytics';
import { MimeType } from '../../../constants/MimeType';

export function* attemptFetchAvailablePortfolioFields({ payload }: ReturnType<typeof fetchDocumentFieldsStarted>) {
    try {
        const availableFields: PortfolioDocumentField[] = yield call(getPortfolioFields, { datasetId: parseInt(payload) });
        yield put(fetchDocumentFieldsSuccessful(availableFields));
    } catch (e) {
        yield put(fetchDocumentFieldsFailed((e as Error).message));
        toast.error('Unable to retrieve the available report fields. Please try again.');
    }
}

function* fetchAvailableReportFieldsWatcher() {
    yield takeLeading(PortfolioAnalyticsActionTypes.FETCH_DOCUMENT_FIELDS_STARTED, attemptFetchAvailablePortfolioFields);
}

export function* checkPortfolioQuestionUpdated({ payload }: ReturnType<typeof updatePortfolioQuestion>) {
    const { key, value } = payload;
    if (key === 'parentDatasetId') {
        if (!isNull(value)) {
            const datasetId = value as number;
            yield put(fetchDocumentFieldsStarted(datasetId.toString()));
        } else {
            yield put(fetchDocumentFieldsSuccessful([]));
        }
    }
}

function* portfolioSurveyUpdatedWatcher() {
    yield takeLatest(PortfolioAnalyticsActionTypes.UPDATE_PORTFOLIO_QUESTION, checkPortfolioQuestionUpdated);
}

export function* attemptAskPortfolioQuestion({ payload }: ReturnType<typeof askPortfolioQuestionStarted>) {
    try {
        const response: PortfolioQuestionResponse = yield call(askPortfolioQuestion, payload);
        yield put(askPortfolioQuestionSuccessful(response));
    } catch (e) {
        const errorMessage = (e as Error).message;
        yield put(askPortfolioQuestionFailed(errorMessage));
        const toastMessage = errorMessage === 'Internal server error' ? 'Unable to ask the question. Please try again.' : errorMessage;
        toast.error(toastMessage);
    }
}

function* askPortfolioQuestionWatcher() {
    yield takeLatest(PortfolioAnalyticsActionTypes.ASK_PORTFOLIO_QUESTION_STARTED, attemptAskPortfolioQuestion);
}

export function* attemptRedirectToDocumentInstance({ payload }: ReturnType<typeof redirectToDocument>) {
    const { documentId, datasetInstanceId, fieldToOpen } = payload;
    yield put(push({ pathname: `/documents/analysis/${documentId}/${datasetInstanceId}`, state: { from: '/admin/intelligent-portfolio-analysis' } }));
    yield put(openDocumentAndInstance(documentId.toString(), datasetInstanceId.toString(), false, fieldToOpen, true));
}

function* goToDocumentInstance() {
    yield takeLeading(PortfolioAnalyticsActionTypes.REDIRECT_TO_DOCUMENT, attemptRedirectToDocumentInstance);
}

export function* attemptDownloadPortfolioAnalytics() {
    try {
        const filename: string = yield select(getDownloadFilename);
        const { results, graphData, summary }: PortfolioQuestionResponse = yield select(getPortfolioResponse);
        const graphKeyColours: GraphKeyAndColour = yield select(getGraphKeyColours);
        const { parentDatasetId, fieldLabel, question, fieldType, datasetType, filters: { documentNameIds } }: PortfolioQuestion = yield select(getPortfolioQuestion);
        const datasets: ClientDatasets[] = yield select(getAllClientDatasets);
        const allDocumentNames: DocumentNameDB[] = yield select(getAllDocumentNames);
        const pieChartBase64: string = yield select(getPieChartBase64);

        const agreementType = datasets.find(({ datasetId }) => datasetId === parentDatasetId)?.agreementType || '';
        const documentNames = allDocumentNames.filter(({ datasetId }) => datasetId === parentDatasetId).filter(({ documentNameId }) => documentNameIds.includes(documentNameId!)).map(({ documentName }) => documentName).join(', ');
        const graphDataWithColour = graphData.map(data => ({ ...data, color: graphKeyColours[data.key] }));
        const formattedFileName = dotsToDashes(filename);
        const doubleRowHeight = navigator.userAgent.indexOf('Win') !== -1;
        const isForm = datasetType === DatasetType.FORM;

        const analytics: Blob = yield call(downloadPortfolioAnalytics, { filename, data: results, graphData: graphDataWithColour, agreementType, documentNames, fieldLabel, question, summary, doubleRowHeight, pieChartBase64, fieldType: fieldType!, isForm });
        yield call(download, analytics, formattedFileName, MimeType.EXCEL);
        yield put(downloadPortfolioAnalyticsSuccessful());
    } catch (e) {
        const errorMessage = (e as Error).message;
        yield put(downloadPortfolioAnalyticsFailed(errorMessage));
        toast.error(errorMessage);
    }
}

function* downloadPortfolioAnalyticsWatcher() {
    yield takeLeading(PortfolioAnalyticsActionTypes.DOWNLOAD_PORTFOLIO_ANALYTICS_STARTED, attemptDownloadPortfolioAnalytics);
}

export function* setDownloadMetaOnModalOpen({ payload }: ReturnType<typeof toggleDownloadModal>) {
    if (isNull(payload)) {
        yield put(setPieChartBase64(''));
        yield put(setFileName(null));
    } else {
        const { svgId, width, height } = payload;
        const { parentDatasetId, fieldLabel }: PortfolioQuestion = yield select(getPortfolioQuestion);
        const datasets: ClientDatasets[] = yield select(getAllClientDatasets);
        const datasetName = datasets.find(({ datasetId }) => datasetId === parentDatasetId)?.agreementType || '';
        const date = formatDate(dateNow());
        const filename = `${datasetName}_${fieldLabel}_${date}`;
        const pieChartBase64: string = yield call(getChartBase64String, svgId, 600, 600, width, height, 600, 600);
        yield put(setPieChartBase64(pieChartBase64));
        yield put(setFileName(filename));
    }
}

function* setDownloadMetaOnModalOpenWatcher() {
    yield takeLeading(PortfolioAnalyticsActionTypes.TOGGLE_DOWNLOAD_MODAL, setDownloadMetaOnModalOpen);
}

export function* attemptFetchAllPortfolioAnalyticsTemplates() {
    try {
        const templates: PortfolioAnalysisTemplate[] = yield call(getPortfolioAnalysisTemplates);
        yield put(getPortfolioAnalyticsTemplatesSuccessful(templates));
    } catch (e) {
        const errorMessage = (e as Error).message;
        yield put(getPortfolioAnalyticsTemplatesFailed(errorMessage));
        toast.error(errorMessage);
    }
}

function* fetchAllPortfolioAnalyticsTemplatesWatcher() {
    yield takeLeading(PortfolioAnalyticsActionTypes.GET_ALL_PORTFOLIO_ANALYTICS_TEMPLATES_STARTED, attemptFetchAllPortfolioAnalyticsTemplates);
}

export function* attemptDeletePortfolioAnalyticsTemplate({ payload }: ReturnType<typeof deletePortfolioAnalyticsTemplateStarted>) {
    try {
        const templates: PortfolioAnalysisTemplate[] = yield call(deletePortfolioAnalysisTemplate, payload);
        yield put(deletePortfolioAnalyticsTemplateSuccessful(templates));
        toast('Successfully deleted template.');
    } catch (e) {
        const errorMessage = (e as Error).message;
        yield put(deletePortfolioAnalyticsTemplateFailed(errorMessage));
        toast.error('Unable to delete the template. Please try again.');
    }
}

function* deletePortfolioAnalyticsTemplate() {
    yield takeLatest(PortfolioAnalyticsActionTypes.DELETE_PORTFOLIO_ANALYTICS_TEMPLATE_STARTED, attemptDeletePortfolioAnalyticsTemplate);
}

export function* attemptSavePortfolioAnalyticsTemplate({ payload }: ReturnType<typeof savePortfolioAnalyticsTemplateStarted>) {
    try {
        const templateUsed: PortfolioAnalysisTemplate = yield select(getPortfolioAnalyticsTemplateUsed);
        const updatedTemplate = { ...templateUsed, templateName: payload.templateName, metaData: payload.metaData };
        const templates: PortfolioAnalysisTemplate[] = yield call(savePortfolioAnalysisTemplate, payload);
        yield put(savePortfolioAnalyticsTemplateSuccessful(templates));
        yield put(setPortfolioAnalyticsTemplate(updatedTemplate));
        toast('Successfully saved template.');
    } catch (e) {
        const errorMessage = (e as Error).message;
        yield put(savePortfolioAnalyticsTemplateFailed(errorMessage));
        toast.error('Unable to save the template. Please try again.');
    }
}

function* savePortfolioAnalyticsTemplate() {
    yield takeLeading(PortfolioAnalyticsActionTypes.SAVE_PORTFOLIO_ANALYTICS_TEMPLATE_STARTED, attemptSavePortfolioAnalyticsTemplate);
}

export function* portfolioAnalyticsSaga() {
    yield all([
        fetchAvailableReportFieldsWatcher(),
        portfolioSurveyUpdatedWatcher(),
        askPortfolioQuestionWatcher(),
        goToDocumentInstance(),
        downloadPortfolioAnalyticsWatcher(),
        setDownloadMetaOnModalOpenWatcher(),
        fetchAllPortfolioAnalyticsTemplatesWatcher(),
        deletePortfolioAnalyticsTemplate(),
        savePortfolioAnalyticsTemplate()
    ]);
}
